import {
    forwardRef,
    RefAttributes,
    useCallback,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from "react";
import type {
    GroupedTableAction,
    GroupedTableInitialState,
    GroupedTableProps,
    ReceiveOp,
} from "./GroupedTable.types";
import { GroupedTableHeader } from "../GroupedTableHeader";
import { GroupedTableViewport } from "../GroupedTableViewport";
import type {
    FetchChildrenGroup,
    GroupedTableApi,
    GroupedTableColumn,
    GroupedTableExportItem,
    GroupedTableInternalContextValue,
    GroupedTableSortState,
    GroupedTableState,
    GroupId,
    GroupNode,
    GroupPath,
    RowEntity,
    RowId,
} from "../types";
import classNames from "classnames";
import {
    buildViewFlatItems,
    GroupedTableInternalContext,
    isGroupExpanded,
    ROOT_ID,
    toMap,
} from "../utils";
import { localize, localizeFormat } from "src/l10n";
import { GroupedTableDataRow } from "../GroupedTableDataRow";
import { GroupedTableLoadingRow } from "../GroupedTableLoadingRow";
import { GroupedTableErrorRow } from "../GroupedTableErrorRow";
import { GroupedTableGroupRow } from "../GroupedTableGroupRow";
import CustomDialog from "src/ui/components/Dialogs/CustomDialog";
import { Conditional } from "src/components/Conditional";

function createInitialState<TRow>(
    groupBy: readonly string[],
    initialState: GroupedTableInitialState = {},
): GroupedTableState<TRow> {
    const root: GroupNode<TRow> = {
        id: ROOT_ID,
        depth: -1,
        path: [],
        label: "",
        parentId: null,
        childGroupIds: [],
        rowIds: [],
        status: "not-loaded",
    };

    return {
        groupBy,
        rootId: ROOT_ID,
        groups: {
            [ROOT_ID]: root,
        },
        rows: {},
        expansion: {
            globalExpandedDepth: initialState.globalExpandedDepth ?? 0,
            collapsed: toMap(initialState.collapsed),
            expanded: toMap(initialState.expanded),
        },
    }
}

function handleReceiveOp<TRow>(
    state: GroupedTableState<TRow>,
    action: ReceiveOp<TRow>,
): GroupedTableState<TRow> {
    const parent = state.groups[action.groupId];
    if (!parent) {
        return state;
    }

    const nextGroups: Record<GroupId, GroupNode<TRow>> = { ...state.groups };
    const nextRows: Record<RowId, RowEntity<TRow>> = { ...state.rows };

    const childGroupIds: GroupId[] = [];
    for (const childGroup of action.childGroups) {
        childGroupIds.push(childGroup.id);

        const existing = nextGroups[childGroup.id];

        nextGroups[childGroup.id] = existing
            ? {
                ...existing,
                path: childGroup.path,
                label: childGroup.label,
                totalCount: childGroup.totalCount,
                depth: childGroup.depth,
                parentId: parent.id,
                values: childGroup.values,
            }
            : {
                id: childGroup.id,
                path: childGroup.path,
                label: childGroup.label,
                totalCount: childGroup.totalCount,
                depth: childGroup.depth,
                parentId: parent.id,
                childGroupIds: [],
                rowIds: [],
                status: "not-loaded",
                values: childGroup.values,
            };
    }

    const rowIds: RowId[] = [];
    for (const row of action.rows) {
        rowIds.push(row.id);
        nextRows[row.id] = {
            id: row.id,
            data: row.data,
            parentGroupId: parent.id,
        };
    }

    nextGroups[parent.id] = {
        ...parent,
        childGroupIds,
        rowIds,
        status: "loaded",
        error: undefined,
    };

    return {
        ...state,
        groups: nextGroups,
        rows: nextRows,
    };
}

function pruneExpandedOverrides<TRow>(
  state: GroupedTableState<TRow>,
): Record<GroupId, true> {
    const next: Record<GroupId, true> = {};
    for (const [gid, v] of Object.entries(state.expansion.expanded)) {
        if (!v) {
            continue
        };

        const g = state.groups[gid];
        if (!g) {
            next[gid] = true;
            continue;
        }
    }
    return next;
}

function reducer<TRow>(
    state: GroupedTableState<TRow>,
    action: GroupedTableAction<TRow>,
): GroupedTableState<TRow> {
    switch (action.type) {
        case "RESET": {
            return createInitialState<TRow>(action.groupBy, action.initialState);
        }

        case "REQUEST_CHILDREN": {
            const group = state.groups[action.groupId];
            if (!group) {
                return state;
            }

            const newGroupNode: GroupNode<TRow> = {
                ...group,
                status: "loading",
                error: undefined,
            };

            return {
                ...state,
                groups: {
                    ...state.groups,
                    [action.groupId]: newGroupNode,
                },
            };
        }

        case "RECEIVE_CHILDREN_SUCCESS":
            return handleReceiveOp(state, action);

        case "RECEIVE_CHILDREN_SUCCESS_BATCH":
            return action.ops.reduce(handleReceiveOp, state);

        case "RECEIVE_CHILDREN_ERROR": {
            const group = state.groups[action.groupId];
            if (!group) {
                return state;
            }

            const newGroupNode: GroupNode<TRow> = {
                ...group,
                status: "error",
                error: action.error,
            };

            return {
                ...state,
                groups: {
                    ...state.groups,
                    [action.groupId]: newGroupNode,
                },
            };
        }

        case "MARK_NOT_LOADED": {
            const group = state.groups[action.groupId];
            if (!group) {
                return state;
            }

            const newGroupNode: GroupNode<TRow> = {
                ...group,
                status: "not-loaded",
                error: undefined,
            };

            return {
                ...state,
                groups: {
                    ...state.groups,
                    [action.groupId]: newGroupNode,
                },
            };
        }

        case "TOGGLE_GROUP": {
            const group = state.groups[action.groupId];
            if (!group || group.id === ROOT_ID) {
                return state;
            }

            const exp = state.expansion;
            const defaultExpanded = group.depth < exp.globalExpandedDepth;
            const currentlyExpanded = isGroupExpanded(group, exp, state.rootId);

            const nextCollapsed = { ...exp.collapsed };
            const nextExpanded = { ...exp.expanded };

            if (currentlyExpanded) {
                if (defaultExpanded) {
                    nextCollapsed[group.id] = true;
                    delete nextExpanded[group.id];
                } else {
                    delete nextExpanded[group.id];
                }
            } else {
                if (defaultExpanded) {
                    delete nextCollapsed[group.id];
                } else {
                    nextExpanded[group.id] = true;
                    delete nextCollapsed[group.id];
                }
            }

            return {
                ...state,
                expansion: {
                    ...exp,
                    expanded: nextExpanded,
                    collapsed: nextCollapsed,
                }
            };
        }

        case "EXPAND_GROUP": {
            const g = state.groups[action.groupId];
            if (!g || g.id === state.rootId) {
                return state;
            }

            const exp = state.expansion;
            const defaultExpanded = g.depth < exp.globalExpandedDepth;

            const nextExpanded = { ...exp.expanded };
            const nextCollapsed = { ...exp.collapsed };

            if (defaultExpanded) {
                delete nextCollapsed[g.id];
            } else {
                nextExpanded[g.id] = true;
            }

            return { ...state, expansion: { ...exp, expanded: nextExpanded, collapsed: nextCollapsed } };
        }

        case "COLLAPSE_GROUP": {
            const g = state.groups[action.groupId];
            if (!g || g.id === state.rootId) {
                return state;
            }

            const exp = state.expansion;
            const defaultExpanded = g.depth < exp.globalExpandedDepth;

            const nextExpanded = { ...exp.expanded };
            const nextCollapsed = { ...exp.collapsed };

            if (defaultExpanded) {
                nextCollapsed[g.id] = true;
            } else {
                delete nextExpanded[g.id];
            }

            return { ...state, expansion: { ...exp, expanded: nextExpanded, collapsed: nextCollapsed } };
        }

        case "EXPAND_ONE_LEVEL": {
            const exp = state.expansion;
            const nextDepth = Math.min(action.maxDepth, exp.globalExpandedDepth + 1);

            return { ...state, expansion: { ...exp, globalExpandedDepth: nextDepth } };
            }

        case "COLLAPSE_ONE_LEVEL": {
            const exp = state.expansion;
            const nextDepth = Math.max(0, exp.globalExpandedDepth - 1);

            return {
                ...state,
                expansion: {
                    ...exp,
                    globalExpandedDepth: nextDepth,
                    expanded: pruneExpandedOverrides(state),
                },
            };
        }

        case "EXPAND_ALL": {
            return {
            ...state,
                expansion: {
                globalExpandedDepth: action.maxDepth,
                    expanded: {},
                    collapsed: {},
                },
            };
        }

        case "COLLAPSE_ALL": {
            return {
                ...state,
                expansion: {
                    globalExpandedDepth: 0,
                    expanded: {},
                    collapsed: {},
                },
            };
        }

        default:
            return state;
    }
}

function mapChildGroups<TRow>(
    childGroup: FetchChildrenGroup<TRow>,
    getGroupId?: ((groupPath: GroupPath) => GroupId) | undefined,
) {
    return {
        id: childGroup.id ?? getGroupId?.(childGroup.path) ?? `${childGroup.path.join("|")}`,
        path: childGroup.path,
        label: childGroup.label,
        totalCount: childGroup.totalCount,
        depth: childGroup.path.length - 1,
        values: childGroup.values,
    };
}

function valueToCsvText(value: unknown): string {
    if (value === undefined || value === null) {
        return "";
    }

    if (value instanceof Date) {
        return value.toISOString();
    }

    if (typeof value === "object") {
        try {
            return JSON.stringify(value);
        } catch {
            return String(value);
        }
    }

    return String(value);
}

function escapeCsvCell(value: unknown): string {
    const text = valueToCsvText(value);
    if (!/[",\r\n]/.test(text)) {
        return text;
    }

    return `"${text.replace(/"/g, "\"\"")}"`;
}

function headerToCsvLabel<TRow>(column: GroupedTableColumn<TRow>): string {
    if (typeof column.header === "string" || typeof column.header === "number") {
        return String(column.header);
    }

    return String(column.id);
}

function getDefaultExportValue<TRow>(
    column: GroupedTableColumn<TRow>,
    columnIndex: number,
    item: GroupedTableExportItem,
    getGroup: (groupId: GroupId) => GroupNode<TRow> | undefined,
    getRow: (rowId: RowId) => RowEntity<TRow> | undefined,
): unknown {
    if (item.kind === "row") {
        const row = getRow(item.id);
        return row?.data[column.id];
    }

    const group = getGroup(item.id);
    const groupValue = group?.values?.[column.id];
    if (groupValue !== undefined && groupValue !== null) {
        return groupValue;
    }

    if (columnIndex === 0) {
        return group?.label ?? "";
    }

    return "";
}

function buildCsv<TRow>(
    columns: readonly GroupedTableColumn<TRow>[],
    items: GroupedTableExportItem[],
    getGroup: (groupId: GroupId) => GroupNode<TRow> | undefined,
    getRow: (rowId: RowId) => RowEntity<TRow> | undefined,
): string {
    const header = columns.map((column) => escapeCsvCell(headerToCsvLabel(column))).join(",");

    const rows = items.map((item) => columns
        .map((column, columnIndex) => {
            const value = column.exportValue
                ? column.exportValue({
                    item,
                    getGroup,
                    getRow,
                })
                : getDefaultExportValue(column, columnIndex, item, getGroup, getRow);
            return escapeCsvCell(value);
        })
        .join(","));

    return [header, ...rows].join("\r\n");
}

function downloadCsvFile(csv: string, fileName: string): void {
    let fileLike: Blob;
    try {
        fileLike = new File([csv], fileName, { type: "text/csv;charset=utf-8;" });
    } catch {
        fileLike = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    }

    const url = URL.createObjectURL(fileLike);
    const openedWindow = window.open(url, "_blank", "noopener,noreferrer");

    if (!openedWindow) {
        const element = document.createElement("a");
        element.href = url;
        element.download = fileName;
        element.style.position = "absolute";
        element.style.left = "-9999px";
        document.body.appendChild(element);

        if (typeof element.click === "function") {
            element.click();
        } else {
            const clickEvent = new MouseEvent("click", {
                bubbles: true,
                cancelable: true,
                view: window,
            });
            element.dispatchEvent(clickEvent);
        }

        window.setTimeout(() => {
            document.body.removeChild(element);
            URL.revokeObjectURL(url);
        }, 1000);
        return;
    }

    window.setTimeout(() => {
        URL.revokeObjectURL(url);
    }, 60000);
}

function getSerializedSort<TRow>(
    sort: GroupedTableSortState<TRow> | undefined,
): { columnId: string; direction: GroupedTableSortState<TRow>["direction"]; } | undefined {
    if (!sort) {
        return undefined;
    }

    return {
        columnId: String(sort.columnId),
        direction: sort.direction,
    };
}

/**
 * GroupedTable component
 *
 * @param {GroupedTableProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function GroupedTableInner<TRow>({
    columns,
    groupBy,
    dataSource,
    className: externalClassName,
    getRowId,
    getGroupId,
    initialState,
    slots,
    maxConcurrentRequests = 4,
    sort,
    defaultSort,
    onSortChange,
    searchText,
    defaultSearchText,
    onSearchTextChange,
    sortingMode = "local",
    filterMode = "local",
}: GroupedTableProps<TRow>, ref: React.Ref<GroupedTableApi | null>): JSX.Element | null {
    const groupByKey = useMemo(() => groupBy.join("|"), [groupBy]);
    const initialStateKey = useMemo(() => JSON.stringify({
        globalExpandedDepth: initialState?.globalExpandedDepth ?? 0,
        expanded: initialState?.expanded ?? [],
        collapsed: initialState?.collapsed ?? [],
    }), [initialState]);

    const [state, dispatch] = useReducer(
        reducer<TRow>,
        undefined,
        () => createInitialState<TRow>(groupBy, initialState),
    );
    const [pendingExportFileName, setPendingExportFileName] = useState<string | null>(null);
    const [uncontrolledSort, setUncontrolledSort] = useState<GroupedTableSortState<TRow> | undefined>(defaultSort);
    const [uncontrolledSearchText, setUncontrolledSearchText] = useState<string>(defaultSearchText ?? "");

    const inFlightRef = useRef<Map<GroupId, AbortController>>(new Map());
    const queryKeyRef = useRef("");

    const effectiveSort = sort ?? uncontrolledSort;
    const effectiveSearchText = searchText ?? uncontrolledSearchText;
    const serverSort = sortingMode === "local"
        ? undefined
        : effectiveSort;
    const serverSearchText = filterMode === "local"
        ? undefined
        : effectiveSearchText;

    const setSortValue = useCallback((nextSort?: GroupedTableSortState<TRow>) => {
        if (sort === undefined) {
            setUncontrolledSort(nextSort);
        }

        onSortChange?.(nextSort);
    }, [onSortChange, sort]);

    const setSearchTextValue = useCallback((nextValue: string) => {
        if (searchText === undefined) {
            setUncontrolledSearchText(nextValue);
        }

        onSearchTextChange?.(nextValue);
    }, [onSearchTextChange, searchText]);

    const resetQueryKey = useMemo(() => JSON.stringify({
        groupBy,
        sort: getSerializedSort(serverSort),
        searchText: serverSearchText,
    }), [groupBy, serverSearchText, serverSort]);

    const abortAllRequests = useCallback(() => {
        for (const ctrl of inFlightRef.current.values()) {
            ctrl.abort();
        }

        inFlightRef.current.clear();
    }, []);

    useEffect(() => {
        queryKeyRef.current = resetQueryKey;
        abortAllRequests();
        dispatch({ type: "RESET", groupBy, initialState });
    }, [abortAllRequests, groupByKey, initialState, initialStateKey, resetQueryKey]);

    // Neat little trick to always have the latest state in async callbacks
    const stateRef = useRef(state); stateRef.current = state;

    const viewState = useMemo(() => buildViewFlatItems(state, {
        columns,
        sort: effectiveSort,
        searchText: effectiveSearchText,
        applyLocalSort: sortingMode !== "server",
        applyLocalFilter: filterMode !== "server",
    }), [columns, effectiveSearchText, effectiveSort, filterMode, sortingMode, state]);
    const flatItems = viewState.flatItems;
    const flatItemsRef = useRef(flatItems); flatItemsRef.current = flatItems;

    const getGroup = useCallback((groupId: GroupId): GroupNode<TRow> | undefined => {
        return stateRef.current.groups[groupId];
    }, []);

    const getRow = useCallback((rowId: RowId): RowEntity<TRow> | undefined => {
        return stateRef.current.rows[rowId];
    }, []);

    const getVisibleExportItems = useCallback((): GroupedTableExportItem[] => {
        return flatItemsRef.current.filter(
            (item) => item.kind === "group" || item.kind === "row"
        );
    }, []);

    const isExpanded = useCallback((groupId: GroupId): boolean => {
        const state = stateRef.current;
        const group = state.groups[groupId];
        if (!group) {
            return false;
        }

        return isGroupExpanded(group, state.expansion, state.rootId);
    }, []);

    const startLoadingChildren = useCallback(
        async (groupId: GroupId, force = false) => {
            const requestQueryKey = queryKeyRef.current;
            const currentState = stateRef.current;
            const group = currentState.groups[groupId];
            if (!group || inFlightRef.current.has(groupId)) {
                return;
            }
            
            if (!force && group.status !== "not-loaded") {
                return;
            }

            const ctrl = new AbortController();
            inFlightRef.current.set(groupId, ctrl);
            dispatch({ type: "REQUEST_CHILDREN", groupId });

            try {
                const result = await dataSource.fetchChildren({
                    groupBy: currentState.groupBy,
                    parentPath: group.path,
                    parentGroupId: groupId,
                    depth: group.depth,
                    signal: ctrl.signal,
                    sort: serverSort,
                    searchText: serverSearchText,
                });

                if (requestQueryKey !== queryKeyRef.current) {
                    return;
                }

                const childGroups = result.groups
                    .map((cg) => mapChildGroups(cg, getGroupId));

                const rows = result.rows.map((row) => ({
                    id: getRowId?.(row.data) ?? row.key,
                    data: row.data,
                }));

                const ops: ReceiveOp<TRow>[] = [
                    { groupId, childGroups, rows },
                ];

                const isBatch = result.groups.some(
                    (group) => group.children && (
                        group.children.groups?.length > 0 ||
                        group.children.rows?.length > 0
                    ),
                );

                if (!isBatch) {
                    dispatch({
                        ...ops[0],
                        type: "RECEIVE_CHILDREN_SUCCESS",
                    });
                    return;
                }

                for (const group of result.groups) {
                    if (!group.children) {
                        continue;
                    }

                    const parentId = group.id ?? getGroupId?.(group.path);
                    if (parentId === undefined) {
                        continue;
                    }

                    ops.push({
                        groupId: parentId,
                        childGroups: (group.children.groups ?? []).map(
                            (cg) => mapChildGroups(cg, getGroupId)),
                        rows: (group.children.rows ?? []).map((row) => ({
                            id: getRowId?.(row.data) ?? row.key,
                            data: row.data,
                        })),
                    });
                }

                dispatch({
                    type: "RECEIVE_CHILDREN_SUCCESS_BATCH",
                    ops,
                });
            } catch (err) {
                if (ctrl.signal.aborted) {
                    return;
                }

                console.error(err);

                dispatch({
                    type: "RECEIVE_CHILDREN_ERROR",
                    groupId,
                    error: localize("TeknisktFel"),
                })
            } finally {
                inFlightRef.current.delete(groupId);
            }
        }, [dataSource, getGroupId, getRowId, serverSearchText, serverSort]);
    
    const api: GroupedTableApi = useMemo(() => {
        const maxDepth = state.groupBy.length;

        return {
            reset: () => {
                queryKeyRef.current = resetQueryKey;
                abortAllRequests();
                dispatch({ type: "RESET", groupBy: state.groupBy, initialState });
            },

            expandOneLevel: () => dispatch({ type: "EXPAND_ONE_LEVEL", maxDepth}),
            collapseOneLevel: () => dispatch({ type: "COLLAPSE_ONE_LEVEL" }),
            expandAll: () => dispatch({ type: "EXPAND_ALL", maxDepth }),
            collapseAll: () => dispatch({ type: "COLLAPSE_ALL" }),
        
            toggleGroup: (groupId) => dispatch({ type: "TOGGLE_GROUP", groupId }),
            expandGroup: (groupId) => dispatch({ type: "EXPAND_GROUP", groupId }),
            collapseGroup: (groupId) => dispatch({ type: "COLLAPSE_GROUP", groupId }),
            setSort: (nextSort) => setSortValue(nextSort),
            setSearchText: (value) => setSearchTextValue(value),

            exportCsv: (fileName) => {
                const defaultFileName = `grouped-table-export-${new Date().toISOString().slice(0, 10)}.csv`;
                setPendingExportFileName(fileName ?? defaultFileName);
            },
        };
    }, [abortAllRequests, initialState, resetQueryKey, setSearchTextValue, setSortValue, state.groupBy]);

    const includedRowCount = getVisibleExportItems().length;
    const totalLoadedRowCount = Object.keys(state.rows).length + 
        Object.values(state.groups).filter((g) => g.id !== ROOT_ID).length;

    const hasPotentialMoreServerData = useMemo(() => {
        return Object.values(state.groups).some((group) => {
            if (group.status !== "loaded") {
                return true;
            }

            return group.totalCount !== undefined
                && group.totalCount > (group.childGroupIds.length + group.rowIds.length);
        });
    }, [state.groups]);

    const resultsMayBePartial = useMemo(() => {
        return hasPotentialMoreServerData && (
            (sortingMode !== "server" && effectiveSort !== undefined) ||
            (filterMode !== "server" && effectiveSearchText.trim().length > 0)
        );
    }, [effectiveSearchText, effectiveSort, filterMode, hasPotentialMoreServerData, sortingMode]);

    const emptyState = useMemo<GroupedTableInternalContextValue<TRow>["emptyState"]>(() => {
        if (flatItems.length > 0) {
            return "empty";
        }

        if (effectiveSearchText.trim().length === 0) {
            return "empty";
        }

        return resultsMayBePartial
            ? "filtered-partial"
            : "filtered-empty";
    }, [effectiveSearchText, flatItems.length, resultsMayBePartial]);

    const closeExportDialog = useCallback(() => {
        setPendingExportFileName(null);
    }, []);

    const confirmExport = useCallback(() => {
        if (!pendingExportFileName) {
            return;
        }

        const csv = buildCsv(columns, getVisibleExportItems(), getGroup, getRow);
        downloadCsvFile(csv, pendingExportFileName);
        setPendingExportFileName(null);
    }, [columns, getGroup, getRow, getVisibleExportItems, pendingExportFileName]);

    useEffect(() => {
        if (!ref) {
            return;
        }
        
        if (typeof ref === "function") {
            ref(api);
        } else if ("current" in ref) {
            (ref as React.MutableRefObject<GroupedTableApi | null>).current = api;
        }
    }, [api, ref]);

    const contextValue = useMemo<GroupedTableInternalContextValue<TRow>>(() => ({
        api,
        columns,
        indentSize: 16,
        sort: effectiveSort,
        searchText: effectiveSearchText,
        sortingMode,
        filterMode,
        setSort: setSortValue,
        setSearchText: setSearchTextValue,
        resultsMayBePartial,
        emptyState,
        onRowClick(row, ids) {
            console.log("onRowClicked", row, ids);
        },
        flatItems,
        getGroup,
        getRow,
        isExpanded,
        slots: {
            DataRow: slots?.DataRow ?? GroupedTableDataRow,
            ErrorRow: slots?.ErrorRow ?? GroupedTableErrorRow,
            GroupRow: slots?.GroupRow ?? GroupedTableGroupRow,
            LoadingRow: slots?.LoadingRow ?? GroupedTableLoadingRow,
            EmptyState: slots?.EmptyState,
        },
    }), [
        api,
        columns,
        effectiveSearchText,
        effectiveSort,
        emptyState,
        filterMode,
        flatItems,
        getGroup,
        getRow,
        isExpanded,
        resultsMayBePartial,
        setSearchTextValue,
        setSortValue,
        sortingMode,
        slots,
    ]);

    useEffect(() => {
        const available = Math.max(0, maxConcurrentRequests - inFlightRef.current.size);
        if (available === 0) {
            return;
        }

        const candidates: GroupId[] = [];
        for (const group of Object.values(state.groups)) {
            if (group.status !== "not-loaded") {
                continue;
            }

            const expanded = isGroupExpanded(group, state.expansion, state.rootId);
            if (!expanded) {
                continue;
            }

            candidates.push(group.id);
        }

        candidates.sort((a, b) => (state.groups[a]?.depth ?? 0) - (state.groups[b]?.depth ?? 0));

        for (const id of candidates.slice(0, available)) {
            void startLoadingChildren(id);
        }
    }, [state.groups, state.expansion, state.rootId, maxConcurrentRequests, startLoadingChildren]);

    useEffect(() => () => { // dispose on unmount
        abortAllRequests();
    }, [abortAllRequests]);

    const className = useMemo(() => classNames(
        "GroupedTable",
        externalClassName,
    ), [externalClassName]);

    return (
        <div className={className}>
            <GroupedTableInternalContext.Provider value={contextValue}>                
                <GroupedTableHeader<TRow>
                    columns={columns}
                    sort={effectiveSort} />
                <div className="GroupedTable-Body">
                    <GroupedTableViewport />
                </div>
            </GroupedTableInternalContext.Provider>
            <CustomDialog
                show={pendingExportFileName !== null}
                title={localize("QH_GROUPED_TABLE_EXPORT_TITLE")}
                message={localizeFormat("QH_GROUPED_TABLE_EXPORT_SELECTION_INFO", includedRowCount, totalLoadedRowCount)}
                confirmMessage={localize("QH_GROUPED_TABLE_EXPORT_CONTINUE")}
                dismissMessage={localize("QH_CANCEL")}
                onDismiss={closeExportDialog}
                onConfirm={confirmExport}>
                    <Conditional condition={hasPotentialMoreServerData}>
                        <div className="GroupedTable-exportWarning">
                            {localize("QH_GROUPED_TABLE_EXPORT_SERVER_WARNING")}
                        </div>
                    </Conditional>
            </CustomDialog>
        </div>
    );
}

const GroupedTable = forwardRef(GroupedTableInner) as <TRow>(
    props: GroupedTableProps<TRow> & RefAttributes<GroupedTableApi | null>,
) => JSX.Element;

export default GroupedTable;

